import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import AddPlayerModal from "../teams/AddPlayerModal";
import { FaExchangeAlt, FaUserMinus, FaEdit } from 'react-icons/fa';
import { FaEnvelope, FaQrcode } from "react-icons/fa6";
import useUserRoles from '../../hooks/useUserRoles';
import { Dropdown, Form } from 'react-bootstrap';
import EditTeamModal from "../teams/EditTeamModal";
import InvitePlayerModal from "../teams/InvitePlayerModal";
import QRCodeModal from "../teams/QRCodeModal";
import { getSeasonOptionLabel } from '../utils/activeSeasonUtil';

function TeamManagerDetailPage({ teamId: propTeamId, onBack }) {
    const { teamId: paramTeamId } = useParams(); // If route param is present
    const teamId = propTeamId || paramTeamId;   // Fallback to prop or param

    const [teamDetails, setTeamDetails] = useState(null);
    const [seasonOptions, setSeasonOptions] = useState([]);
    const [selectedSeason, setSelectedSeason] = useState('');
    const [loading, setLoading] = useState(true);
    const [teamLoading, setTeamLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const { isCaptain, isCommissioner } = useUserRoles();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);


    useEffect(() => {
        let cancelled = false;

        const loadTeamDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get(`/api/teams/${teamId}/`, {
                    withCredentials: true,
                });

                if (cancelled) {
                    return;
                }

                const baseTeam = response.data;
                const attachedSeasons = baseTeam.attached_seasons || [];
                const defaultSeason = baseTeam.current_season?.toString() || attachedSeasons[0]?.id?.toString() || '';

                setSeasonOptions(attachedSeasons);
                setSelectedSeason(defaultSeason);

                if (defaultSeason) {
                    const seasonResponse = await axios.get(`/api/teams/${teamId}/`, {
                        params: { season: defaultSeason },
                        withCredentials: true,
                    });

                    if (cancelled) {
                        return;
                    }

                    setTeamDetails(seasonResponse.data);
                    setSeasonOptions(seasonResponse.data.attached_seasons || attachedSeasons);
                } else {
                    setTeamDetails(baseTeam);
                }
            } catch (loadError) {
                if (cancelled) {
                    return;
                }
                console.error("Error fetching team details:", loadError);
                setError("Failed to load team details.");
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadTeamDetails();

        return () => {
            cancelled = true;
        };
    }, [teamId]);

    const fetchTeamDetails = async (seasonId = selectedSeason) => {
        try {
            setTeamLoading(true);
            setError(null);
            const response = await axios.get(`/api/teams/${teamId}/`, {
                params: seasonId ? { season: seasonId } : {},
                withCredentials: true,
            });
            setTeamDetails(response.data);
            if (response.data.attached_seasons) {
                setSeasonOptions(response.data.attached_seasons);
            }
        } catch (fetchError) {
            console.error("Error fetching team details:", fetchError);
            setError("Failed to load team details.");
        } finally {
            setTeamLoading(false);
        }
    };

    const handleSeasonChange = async (event) => {
        const seasonId = event.target.value;
        setSelectedSeason(seasonId);
        if (seasonId) {
            await fetchTeamDetails(seasonId);
        }
    };

    const refreshSelectedSeason = async () => {
        await fetchTeamDetails(selectedSeason);
    };

    const handlePlayerAdded = async () => {
        setShowAddModal(false);
        setShowTransferModal(false);
        setShowRemoveModal(false);
        await refreshSelectedSeason();
    };

    const handlePlayerAction = (player, action) => {
        setSelectedPlayer(player);
        if (action === 'transfer') {
            setShowTransferModal(true);
        } else if (action === 'remove') {
            setShowRemoveModal(true);
        }
    };

    const handlePlayerRemoved = async () => {
        try {
            await axios.delete(`/api/teams/${teamId}/players/`, {
                withCredentials: true,
                params: {
                    player_id: selectedPlayer.id
                }
            });
            setShowRemoveModal(false);
            setSelectedPlayer(null);
            await refreshSelectedSeason();
        } catch (error) {
            console.error('Error removing player:', error);
            setError('Failed to remove player from team');
        }
    };

    if (loading) {
        return <div className="container">Loading team details...</div>;
    }

    if (error) {
        return <div className="container text-danger">{error}</div>;
    }

    return (
        <>
            <div className="container">
                {/* Card START */}
                <div className="card mb-3 p-0">
                    <div
                        className="h-200px rounded-top"
                        style={{
                            backgroundColor: teamDetails.color || '#c93452',
                            backgroundPosition: "center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                        }}
                    ></div>

                    {/* Card body START */}
                    <div className="card-body py-0">
                        <div className="d-sm-flex align-items-start text-center text-sm-start">
                            <div>
                                <div className="avatar avatar-xxl mt-n5 mb-3">
                                    <img
                                        className="avatar-img rounded-circle border border-white border-3"
                                        src={
                                            teamDetails.logo ||
                                            `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                        }
                                        alt={`${teamDetails.name} logo`}
                                        style={{ backgroundColor: "white" }}
                                    />
                                </div>
                            </div>
                            <div className="ms-sm-4 mt-sm-3">
                                <div className="row">
                                    <div className="col">
                                        <h3 className="mb-0">{teamDetails.name}</h3>
                                    </div>
                                </div>
                                <p className="mb-0 text-muted">
                                    {teamDetails.league_name ? `League: ${teamDetails.league_name}` : 'No league assigned'}
                                </p>
                                <p className="mb-0 text-muted">
                                    {teamDetails.color_name ? `Color: ${teamDetails.color_name}` : 'No color assigned'}
                                </p>
                            </div>
                            <div className="d-flex mt-3 justify-content-center ms-sm-auto">
                                {(isCommissioner || isCaptain) && (
                                    <Dropdown>
                                        <Dropdown.Toggle bsPrefix="dropdown-icon" variant="light" id="profileAction2">
                                            <i className="bi bi-three-dots"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setShowEditModal(true)}>
                                                <FaEdit className="me-1" /> Edit Team
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => setShowInviteModal(true)}>
                                                <FaEnvelope className="me-1" /> Invite Players
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => setShowQRModal(true)}>
                                                <FaQrcode className="me-1" /> QR Code
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Card body END */}
                </div>
                {/* Card END */}

                {/* Edit Team Modal */}
                <EditTeamModal
                    show={showEditModal}
                    handleClose={() => setShowEditModal(false)}
                    team={teamDetails}
                    onTeamUpdated={(updatedTeam) => {
                        setTeamDetails(updatedTeam);
                        setShowEditModal(false);
                    }}
                />

                {/* Invite Players Modal */}
                <InvitePlayerModal
                    show={showInviteModal}
                    handleClose={() => setShowInviteModal(false)}
                    team={teamDetails}
                    season={selectedSeason || teamDetails?.current_season}
                />

                <QRCodeModal
                    show={showQRModal}
                    handleClose={() => setShowQRModal(false)}
                    team={teamDetails}
                    season={selectedSeason || teamDetails?.current_season}
                />

                {/* Team Details */}
                <div className="card card-body mb-3">
                    <div className="row g-4">
                        {/* Team Stats */}
                        <div className="col-sm-6 col-lg-4">
                            <h5>Team Stats</h5>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Wins:</span>
                                <span className="text-success">{teamDetails.wins}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Losses:</span>
                                <span className="text-danger">{teamDetails.losses}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Draws:</span>
                                <span className="text-warning">{teamDetails.draws}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Total Games:</span>
                                <span className="text-primary">{teamDetails.total_games}</span>
                            </div>
                        </div>

                        {/* Team Leadership */}
                        <div className="col-sm-6 col-lg-4">
                            <h5>Team Leadership</h5>
                            <div className="mb-3">
                                <h6>Captain</h6>
                                {teamDetails.captain_name ? (
                                    <div className="d-flex align-items-center">
                                        <img
                                            src={teamDetails.captain_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                            alt="Captain"
                                            className="rounded-circle me-2"
                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                        />
                                        <span>{teamDetails.captain_name}</span>
                                    </div>
                                ) : (
                                    <p className="text-muted small">No captain assigned</p>
                                )}
                            </div>
                            <div>
                                <h6>Assistant Captain</h6>
                                {teamDetails.a_cap_name ? (
                                    <div className="d-flex align-items-center">
                                        <img
                                            src={teamDetails.a_cap_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                            alt="Assistant Captain"
                                            className="rounded-circle me-2"
                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                        />
                                        <span>{teamDetails.a_cap_name}</span>
                                    </div>
                                ) : (
                                    <p className="text-muted small">No assistant captain assigned</p>
                                )}
                            </div>
                        </div>

                        {/* Team Players */}
                        <div className="col-12">
                            <div className="row align-items-end mb-4 g-3">
                                <div className="col">
                                    <h2>Players ({teamDetails.players?.length || 0})</h2>
                                </div>
                                <div className="col-md-4 col-lg-3">
                                    <Form.Group controlId="manager-team-detail-season-select">
                                        <Form.Label>Season</Form.Label>
                                        <Form.Select
                                            value={selectedSeason}
                                            onChange={handleSeasonChange}
                                            disabled={teamLoading || seasonOptions.length === 0}
                                        >
                                            {seasonOptions.length === 0 ? (
                                                <option value="">No seasons</option>
                                            ) : (
                                                seasonOptions.map((season) => (
                                                    <option key={season.id} value={season.id}>
                                                        {getSeasonOptionLabel(season)}
                                                    </option>
                                                ))
                                            )}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="col-auto">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowAddModal(true)}
                                    >
                                        Add Player
                                    </button>
                                </div>
                            </div>
                            {teamLoading && (
                                <div className="text-muted mb-3">Loading players...</div>
                            )}
                            <div className="row g-4">
                                {teamDetails.players?.length === 0 && !teamLoading ? (
                                    <div className="col-12">
                                        <div className="text-muted">No players found for this season.</div>
                                    </div>
                                ) : teamDetails.players?.map((player) => (
                                    <div key={player.id} className="col-sm-6 col-lg-4">
                                        <div className="card h-100">
                                            <div className="card-body">
                                                <div className="d-flex flex-column">
                                                    <div className="d-flex align-items-center mb-3">
                                                        <img
                                                            src={player.profile_picture || `/static/images/kb_static/lemont_rec_leagues_circle.png`}
                                                            alt={`${player.first_name} ${player.last_name}`}
                                                            className="rounded-circle me-3"
                                                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                        />
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">
                                                                <Link to={`/league-manager/players/${player.user_id}`} className="text-decoration-none">
                                                                    {player.first_name} {player.last_name}
                                                                </Link>
                                                            </h6>
                                                            <small className="text-muted">
                                                                {teamDetails.captain === player.id ? 'Captain' :
                                                                    teamDetails.a_cap === player.id ? 'Assistant Captain' :
                                                                        'Player'}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                                        <button
                                                            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                                            onClick={() => handlePlayerAction(player, 'transfer')}
                                                            title="Transfer Player"
                                                        >
                                                            <FaExchangeAlt size={14} />
                                                            <span>Transfer</span>
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                                                            onClick={() => handlePlayerAction(player, 'remove')}
                                                            title="Remove Player"
                                                        >
                                                            <FaUserMinus size={14} />
                                                            <span>Remove</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Player Modal */}
            <AddPlayerModal
                teamId={teamId}
                onPlayerAdded={handlePlayerAdded}
                mode="add"
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
            />

            {/* Transfer Player Modal */}
            <AddPlayerModal
                teamId={teamId}
                onPlayerAdded={handlePlayerAdded}
                mode="transfer"
                show={showTransferModal}
                onHide={() => {
                    setShowTransferModal(false);
                    setSelectedPlayer(null);
                }}
                selectedPlayer={selectedPlayer}
            />

            {/* Remove Player Confirmation Modal */}
            <div className={`modal ${showRemoveModal ? 'show d-block' : 'd-none'}`}
                tabIndex="-1"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Remove Player</h5>
                            <button type="button" className="btn-close" onClick={() => {
                                setShowRemoveModal(false);
                                setSelectedPlayer(null);
                            }}></button>
                        </div>
                        <div className="modal-body">
                            {selectedPlayer && (
                                <p>Are you sure you want to remove {selectedPlayer.first_name} {selectedPlayer.last_name} from the team?</p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => {
                                setShowRemoveModal(false);
                                setSelectedPlayer(null);
                            }}>Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={handlePlayerRemoved}>
                                Remove Player
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default TeamManagerDetailPage;
